import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Only English is bundled – every other locale is loaded on demand via dynamic import.
import en from './locales/en.json';

// Lazy-import map – Vite code-splits each into its own chunk.
const localeLoaders: Record<string, () => Promise<{ default: any }>> = {
  es: () => import('./locales/es.json'),
  fr: () => import('./locales/fr.json'),
  de: () => import('./locales/de.json'),
  it: () => import('./locales/it.json'),
  tr: () => import('./locales/tr.json'),
  ar: () => import('./locales/ar.json'),
  bn: () => import('./locales/bn.json'),
  pt: () => import('./locales/pt.json'),
};

/**
 * Minimal i18next backend plugin that resolves translations from
 * Vite dynamic imports. Falls through to the bundled `en` resource
 * when no loader is registered (i.e. for English).
 */
const LazyImportBackend = {
  type: 'backend' as const,
  init() {},
  read(language: string, _namespace: string, callback: (err: Error | null, data?: Record<string, string>) => void) {
    const loader = localeLoaders[language];
    if (!loader) {
      // Language already bundled (en) or unsupported – nothing to load.
      callback(null, {});
      return;
    }
    loader()
      .then((mod) => callback(null, mod.default))
      .catch((err) => callback(err));
  },
};

// Initialize i18next
i18n
  // Detect user language
  // Learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // Lazy backend for non-English locales
  .use(LazyImportBackend)
  // Pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // Initialize i18next
  // For all options read: https://www.i18next.com/overview/configuration-options
  .init({
    // Bundle only English; other languages loaded by LazyImportBackend.
    partialBundledLanguages: true,
    resources: {
      en: { translation: en },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'fr', 'de', 'it', 'tr', 'ar', 'bn', 'pt'],
    load: 'languageOnly',
    returnEmptyString: false,
    
    detection: {
      // ONLY detect from path and htmlTag on initial load to guarantee hydration matches the prerendered HTML.
      // Do NOT detect from cookie/localStorage/navigator synchronously, or it will cause React #418 mismatch.
      order: ['path', 'htmlTag'],
      // Keys or params to lookup language from
      lookupCookie: 'i18next',
      // Cache user language on
      caches: [],
      cookieMinutes: 10080, // 7 days
    },

    interpolation: {
      escapeValue: false, // Not needed for react as it escapes by default
    }
  });

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  document.documentElement.dir = i18n.dir(lng);
  document.cookie = `i18next=${lng}; path=/; max-age=31536000`; // 1 year
});

export default i18n;

